import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  User, 
  Building2, 
  Mail, 
  Phone, 
  MapPin,
  Calendar,
  FileText,
  Crown,
  UserCheck,
  UserX,
  UserPlus,
  Car,
  Plus,
  X,
  ShoppingCart,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCustomerForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [cars, setCars] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    street: '',
    zip_code: '',
    city: '',
    country: 'Österreich',
    company: '',
    customer_type: 'private',
    date_of_birth: '',
    tax_id: '',
    status: 'active',
    preferred_contact: 'email',
    notes: '',
    interested_cars: [],
    purchased_cars: []
  });

  useEffect(() => {
    fetchCars();
    if (isEditing) {
      fetchCustomer();
    }
  }, [id]);

  const fetchCars = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cars`);
      if (res.ok) {
        const data = await res.json();
        setCars(data);
      }
    } catch (error) {
      console.error('Error fetching cars:', error);
    }
  };

  const fetchCustomer = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/customers/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFormData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          mobile: data.mobile || '',
          street: data.street || '',
          zip_code: data.zip_code || '',
          city: data.city || '',
          country: data.country || 'Österreich',
          company: data.company || '',
          customer_type: data.customer_type || 'private',
          date_of_birth: data.date_of_birth || '',
          tax_id: data.tax_id || '',
          status: data.status || 'active',
          preferred_contact: data.preferred_contact || 'email',
          notes: data.notes || '',
          interested_cars: data.interested_cars || [],
          purchased_cars: data.purchased_cars || []
        });
      } else {
        toast.error('Kunde nicht gefunden');
        navigate('/admin/customers');
      }
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name) {
      toast.error('Bitte Vor- und Nachname eingeben');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = isEditing 
        ? `${API_URL}/api/customers/${id}` 
        : `${API_URL}/api/customers`;
      
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(isEditing ? 'Kunde aktualisiert' : 'Kunde erstellt');
        navigate(`/admin/customers/${data.id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/customers" className="p-2 hover:bg-[#F9F9F7] rounded transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-2xl font-heading">
            {isEditing ? 'Kunde bearbeiten' : 'Neuer Kunde'}
          </h1>
        </div>
        <Button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
          {saving ? <div className="spinner-sm" /> : <Save size={18} />}
          Speichern
        </Button>
      </div>

      {/* Basic Info */}
      <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
        <h2 className="font-heading text-lg mb-6 flex items-center gap-2">
          <User size={20} />
          Grunddaten
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="overline block mb-2">Vorname *</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              className="input-premium w-full"
              required
              data-testid="customer-first-name"
            />
          </div>
          <div>
            <label className="overline block mb-2">Nachname *</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              className="input-premium w-full"
              required
              data-testid="customer-last-name"
            />
          </div>
          <div>
            <label className="overline block mb-2">Geburtsdatum</label>
            <input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => handleChange('date_of_birth', e.target.value)}
              className="input-premium w-full"
            />
          </div>
          <div>
            <label className="overline block mb-2">Kundentyp</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="customer_type"
                  value="private"
                  checked={formData.customer_type === 'private'}
                  onChange={(e) => handleChange('customer_type', e.target.value)}
                  className="accent-gold"
                />
                <User size={16} />
                Privat
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="customer_type"
                  value="business"
                  checked={formData.customer_type === 'business'}
                  onChange={(e) => handleChange('customer_type', e.target.value)}
                  className="accent-gold"
                />
                <Building2 size={16} />
                Geschäftlich
              </label>
            </div>
          </div>
        </div>

        {formData.customer_type === 'business' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-[#E5E5E5]">
            <div>
              <label className="overline block mb-2">Firmenname</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => handleChange('company', e.target.value)}
                className="input-premium w-full"
                placeholder="Firmenname GmbH"
              />
            </div>
            <div>
              <label className="overline block mb-2">Steuernummer / UID</label>
              <input
                type="text"
                value={formData.tax_id}
                onChange={(e) => handleChange('tax_id', e.target.value)}
                className="input-premium w-full"
                placeholder="ATU12345678"
              />
            </div>
          </div>
        )}
      </div>

      {/* Contact Info */}
      <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
        <h2 className="font-heading text-lg mb-6 flex items-center gap-2">
          <Phone size={20} />
          Kontaktdaten
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="overline block mb-2">E-Mail</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="input-premium w-full"
              data-testid="customer-email"
            />
          </div>
          <div>
            <label className="overline block mb-2">Bevorzugter Kontakt</label>
            <select
              value={formData.preferred_contact}
              onChange={(e) => handleChange('preferred_contact', e.target.value)}
              className="input-premium w-full"
            >
              <option value="email">E-Mail</option>
              <option value="phone">Telefon</option>
              <option value="mobile">Mobil</option>
            </select>
          </div>
          <div>
            <label className="overline block mb-2">Telefon</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className="input-premium w-full"
              placeholder="+43 1 234 5678"
            />
          </div>
          <div>
            <label className="overline block mb-2">Mobil</label>
            <input
              type="tel"
              value={formData.mobile}
              onChange={(e) => handleChange('mobile', e.target.value)}
              className="input-premium w-full"
              placeholder="+43 660 123 4567"
            />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
        <h2 className="font-heading text-lg mb-6 flex items-center gap-2">
          <MapPin size={20} />
          Adresse
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="overline block mb-2">Straße & Hausnummer</label>
            <input
              type="text"
              value={formData.street}
              onChange={(e) => handleChange('street', e.target.value)}
              className="input-premium w-full"
              placeholder="Musterstraße 123"
            />
          </div>
          <div>
            <label className="overline block mb-2">PLZ</label>
            <input
              type="text"
              value={formData.zip_code}
              onChange={(e) => handleChange('zip_code', e.target.value)}
              className="input-premium w-full"
              placeholder="1010"
            />
          </div>
          <div>
            <label className="overline block mb-2">Stadt</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              className="input-premium w-full"
              placeholder="Wien"
            />
          </div>
          <div>
            <label className="overline block mb-2">Land</label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => handleChange('country', e.target.value)}
              className="input-premium w-full"
            />
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
        <h2 className="font-heading text-lg mb-6">Status</h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: 'active', label: 'Aktiv', icon: UserCheck, color: 'green' },
            { value: 'vip', label: 'VIP', icon: Crown, color: 'amber' },
            { value: 'prospect', label: 'Interessent', icon: UserPlus, color: 'blue' },
            { value: 'inactive', label: 'Inaktiv', icon: UserX, color: 'gray' }
          ].map((status) => (
            <label
              key={status.value}
              className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                formData.status === status.value
                  ? `border-${status.color}-500 bg-${status.color}-50`
                  : 'border-[#E5E5E5] hover:border-[#CCCCCC]'
              }`}
            >
              <input
                type="radio"
                name="status"
                value={status.value}
                checked={formData.status === status.value}
                onChange={(e) => handleChange('status', e.target.value)}
                className="sr-only"
              />
              <status.icon size={20} className={formData.status === status.value ? `text-${status.color}-600` : 'text-[#666666]'} />
              <span className={formData.status === status.value ? `text-${status.color}-800 font-medium` : ''}>
                {status.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Fahrzeug-Verknüpfung */}
      <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
        <h2 className="font-heading text-lg mb-6 flex items-center gap-2">
          <Car size={20} />
          Fahrzeuge
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Interessierte Fahrzeuge */}
          <div>
            <label className="overline block mb-2 flex items-center gap-2">
              <Heart size={14} className="text-rose-500" />
              Interessiert an
            </label>
            <select
              onChange={(e) => {
                const carId = e.target.value;
                if (carId && !formData.interested_cars.includes(carId)) {
                  handleChange('interested_cars', [...formData.interested_cars, carId]);
                }
                e.target.value = '';
              }}
              className="input-premium w-full mb-3"
              data-testid="interested-cars-select"
            >
              <option value="">Fahrzeug hinzufügen...</option>
              {cars
                .filter(car => !formData.interested_cars.includes(car.id) && !formData.purchased_cars.includes(car.id))
                .map(car => (
                  <option key={car.id} value={car.id}>
                    {car.brand} {car.model} ({car.year}) - €{car.price.toLocaleString()}
                  </option>
                ))
              }
            </select>
            
            {formData.interested_cars.length > 0 ? (
              <div className="space-y-2">
                {formData.interested_cars.map(carId => {
                  const car = cars.find(c => c.id === carId);
                  if (!car) return null;
                  return (
                    <div key={carId} className="flex items-center justify-between p-3 bg-rose-50 border border-rose-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Heart size={16} className="text-rose-500" />
                        <div>
                          <p className="font-medium text-sm">{car.brand} {car.model}</p>
                          <p className="text-xs text-[#666666]">{car.year} • €{car.price.toLocaleString()}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleChange('interested_cars', formData.interested_cars.filter(id => id !== carId))}
                        className="p-1 hover:bg-rose-100 rounded text-rose-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-[#999999] italic">Keine Fahrzeuge ausgewählt</p>
            )}
          </div>

          {/* Gekaufte Fahrzeuge */}
          <div>
            <label className="overline block mb-2 flex items-center gap-2">
              <ShoppingCart size={14} className="text-emerald-500" />
              Gekauft
            </label>
            <select
              onChange={(e) => {
                const carId = e.target.value;
                if (carId && !formData.purchased_cars.includes(carId)) {
                  handleChange('purchased_cars', [...formData.purchased_cars, carId]);
                }
                e.target.value = '';
              }}
              className="input-premium w-full mb-3"
              data-testid="purchased-cars-select"
            >
              <option value="">Fahrzeug hinzufügen...</option>
              {cars
                .filter(car => !formData.purchased_cars.includes(car.id) && !formData.interested_cars.includes(car.id))
                .map(car => (
                  <option key={car.id} value={car.id}>
                    {car.brand} {car.model} ({car.year}) - €{car.price.toLocaleString()}
                  </option>
                ))
              }
            </select>
            
            {formData.purchased_cars.length > 0 ? (
              <div className="space-y-2">
                {formData.purchased_cars.map(carId => {
                  const car = cars.find(c => c.id === carId);
                  if (!car) return null;
                  return (
                    <div key={carId} className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <ShoppingCart size={16} className="text-emerald-500" />
                        <div>
                          <p className="font-medium text-sm">{car.brand} {car.model}</p>
                          <p className="text-xs text-[#666666]">{car.year} • €{car.price.toLocaleString()}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleChange('purchased_cars', formData.purchased_cars.filter(id => id !== carId))}
                        className="p-1 hover:bg-emerald-100 rounded text-emerald-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-[#999999] italic">Keine Fahrzeuge ausgewählt</p>
            )}
          </div>
        </div>
        
        <p className="text-xs text-[#999999] mt-4">
          Tipp: Sie können von "Interessiert" zu "Gekauft" wechseln, indem Sie das Fahrzeug entfernen und neu hinzufügen.
        </p>
      </div>

      {/* Notes */}
      <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
        <h2 className="font-heading text-lg mb-6 flex items-center gap-2">
          <FileText size={20} />
          Interne Notizen
        </h2>

        <textarea
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          className="input-premium w-full h-32 resize-y"
          placeholder="Interne Notizen zum Kunden..."
        />
      </div>

      {/* Submit Button (Mobile) */}
      <div className="md:hidden">
        <Button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={saving}>
          {saving ? <div className="spinner-sm" /> : <Save size={18} />}
          Speichern
        </Button>
      </div>
    </form>
  );
}
